import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Form, InputNumber, Modal, Select, Space, message } from 'antd';
import { useState } from 'react';
import { FaBuildingColumns, FaMobile } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import axios from '../config/axios';
import { PaymentAccount } from '../types/payment-account.types';

interface WithdrawModalProps {
	open: boolean;
	onClose: () => void;
	availableBalance: number;
	onSuccess?: () => void;
}

interface WithdrawFormValues {
	amount: number;
	payment_account_id: number;
}

interface ErrorResponse {
	response?: {
		data?: {
			msg?: string;
		};
	};
	message?: string;
}

const RetailerWithdrawModal = ({ open, onClose, availableBalance, onSuccess }: WithdrawModalProps) => {
	const [form] = Form.useForm<WithdrawFormValues>();
	const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
	const navigate = useNavigate();

	// Fetch active payment accounts
	const { data: accountsData, isLoading: isLoadingAccounts } = useQuery<{ results: PaymentAccount[] }>({
		queryKey: ['paymentAccounts'],
		queryFn: async () => {
			const { data } = await axios.get('/retailers/payment-accounts', { withCredentials: true });
			return data;
		},
		enabled: open, // Only fetch when modal is open
	});

	// Filter for active accounts
	const activeAccounts = accountsData?.results.filter((account) => account.is_active) || [];

	// Request withdrawal mutation
	const { mutate: requestWithdrawal, isPending } = useMutation({
		mutationFn: async (values: WithdrawFormValues) => {
			const { data } = await axios.post('/payments/retailers/withdraw', values, { withCredentials: true });
			return data;
		},
		onSuccess: () => {
			message.success('Withdrawal request submitted successfully');
			form.resetFields();
			setSelectedAmount(null);
			if (onSuccess) {
				onSuccess();
			}
			onClose();

			// Redirect to withdrawals page
			navigate('/retailer/withdrawals');
		},
		onError: (error: ErrorResponse) => {
			console.error('Withdrawal request failed:', error);
			message.error(error.response?.data?.msg || 'Failed to process withdrawal request');
		},
	});

	const handleSubmit = (values: WithdrawFormValues) => {
		if (values.amount > availableBalance) {
			return message.error('Withdrawal amount cannot exceed available balance');
		}
		requestWithdrawal(values);
	};

	const handleClose = () => {
		form.resetFields();
		setSelectedAmount(null);
		onClose();
	};

	// Quick amount selection buttons
	const quickAmounts = [
		{ label: '25%', value: Math.floor(availableBalance * 0.25) },
		{ label: '50%', value: Math.floor(availableBalance * 0.5) },
		{ label: '75%', value: Math.floor(availableBalance * 0.75) },
		{ label: 'Max', value: Math.floor(availableBalance) },
	];

	return (
		<Modal
			title="Withdraw Funds"
			open={open}
			onCancel={handleClose}
			footer={null}
			destroyOnClose
		>
			{activeAccounts.length === 0 && !isLoadingAccounts ? (
				<div className="text-center">
					<p className="mb-4 text-gray-500">No active payment accounts found. Please add an active payment account first.</p>
					<Button
						type="primary"
						onClick={handleClose}
					>
						Close
					</Button>
				</div>
			) : (
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					requiredMark={false}
				>
					<div className="mb-4 rounded-md bg-blue-50 p-3 text-blue-700">
						<p className="font-medium">
							Available balance:{' '}
							{new Intl.NumberFormat('en-US', {
								style: 'currency',
								currency: 'UGX',
								minimumFractionDigits: 0,
								maximumFractionDigits: 0,
							}).format(availableBalance)}
						</p>
					</div>

					<Form.Item
						name="payment_account_id"
						label="Select Payment Account"
						rules={[{ required: true, message: 'Please select a payment account' }]}
					>
						<Select
							loading={isLoadingAccounts}
							placeholder="Select payment account"
							optionLabelProp="label"
						>
							{activeAccounts.map((account) => (
								<Select.Option
									key={account.id}
									value={account.id}
									label={`${account.account_number} ${account.type === 'bank' ? `(${account.bank_name})` : ''}`}
								>
									<div className="flex items-center gap-2">
										{account.type === 'mobile_money' ? (
											<FaMobile className="text-purple-600" />
										) : (
											<FaBuildingColumns className="text-blue-600" />
										)}
										<span>{account.account_number}</span>
										{account.type === 'bank' && <span className="text-gray-500">({account.bank_name})</span>}
									</div>
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item
						name="amount"
						label="Amount to Withdraw"
						rules={[
							{ required: true, message: 'Please enter an amount to withdraw' },
							{
								validator: (_, value) => {
									if (value > availableBalance) {
										return Promise.reject('Amount exceeds available balance');
									}
									if (value <= 0) {
										return Promise.reject('Amount must be greater than zero');
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<InputNumber
							style={{ width: '100%' }}
							placeholder="Enter amount"
							min={1}
							max={availableBalance}
							formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
							parser={(value: string | undefined) => {
								if (!value) return 0;
								return Number(value.replace(/\$\s?|(,*)/g, ''));
							}}
							value={selectedAmount}
							onChange={(value) => setSelectedAmount(value)}
							addonBefore="UGX"
						/>
					</Form.Item>

					<div className="mb-4">
						<p className="mb-2 text-sm text-gray-500">Quick select:</p>
						<Space>
							{quickAmounts.map((option, index) => (
								<Button
									key={index}
									onClick={() => {
										setSelectedAmount(option.value);
										form.setFieldsValue({ amount: option.value });
									}}
									disabled={option.value <= 0}
								>
									{option.label}
								</Button>
							))}
						</Space>
					</div>

					<div className="flex justify-end space-x-3">
						<Button onClick={handleClose}>Cancel</Button>
						<Button
							type="primary"
							htmlType="submit"
							loading={isPending}
							disabled={isPending || isLoadingAccounts || activeAccounts.length === 0}
						>
							Withdraw
						</Button>
					</div>
				</Form>
			)}
		</Modal>
	);
};

export default RetailerWithdrawModal;
